import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from 'components/ModalV2/Modal'
import { BigText } from 'styling/TypographyV2'
import { Button, Flex } from 'styling/GlobalStyles'
import { Input } from 'components/Form'
import { useState } from 'react'
import {
  InventoryTrackingMode,
  ProcurementPurchaseOrderItemResponse,
  ProcurementPurchaseOrderResponse,
  ProcurementPurchaseOrderStatus,
  ReceiveProcurementPurchaseOrderItemRequest,
  ReceiveProcurementPurchaseOrderItemRequestItem,
  ReceiveProcurementPurchaseOrderItemRequestSerial,
} from 'types/procurement'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'utils/axios'
import { Spinner } from 'components/Loading/Loading'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { LocationSelect } from 'components/Select/LocationSelect'
import { receiveProcurementPurchaseOrderItem } from 'api'
import CalendarInput from 'components/CalendarInput/CalendarInput'

export const ReceiveItemModal = ({
  onClose,
  item,
  order,
  setActiveOrder,
}: {
  onClose: () => void
  item: ProcurementPurchaseOrderItemResponse
  order: ProcurementPurchaseOrderResponse
  setActiveOrder: (order: ProcurementPurchaseOrderResponse) => void
}) => {
  const queryClient = useQueryClient()
  const [values, setValues] = useState({
    expiryValue: '',
    batchCode: '',
    quantity:
      item.procurementPurchaseRequestItem.inventoryItem.trackingMode ===
      InventoryTrackingMode.Serial
        ? 1
        : '',
    mfgDate: null,
    location: null,
    serialNumber: '',
    warrantyExpiry: null,
  })

  const mutation = useMutation({
    mutationFn: async () => {
      try {
        if (!values.location) {
          toast.error('Location is required')
          return null
        }
        if (!values.quantity || Number(values.quantity) <= 0) {
          toast.error('Quantity is required')
          return null
        }

        const itemData: ReceiveProcurementPurchaseOrderItemRequestItem = {
          procurementPurchaseOrderItemId: item.id,
          quantity: Number(values.quantity),
        }
        if (
          item.procurementPurchaseRequestItem.inventoryItem.trackingMode ===
          InventoryTrackingMode.Serial
        ) {
          if (!values.serialNumber) {
            toast.error('Serial Number is required')
            return null
          }
          const sirialNo: ReceiveProcurementPurchaseOrderItemRequestSerial = {
            serialNo: values.serialNumber,
          }
          if (values.warrantyExpiry) {
            sirialNo.warrantyExpiry = values.warrantyExpiry
          }

          itemData.serials = [sirialNo]
        }
        if (values.expiryValue) {
          itemData.expiryValue = Number(values.expiryValue)
        }
        if (values.batchCode) {
          itemData.batchCode = values.batchCode
        }
        if (values.mfgDate) {
          itemData.mfgDate = values.mfgDate
        }
        const data: ReceiveProcurementPurchaseOrderItemRequest = {
          locationId: values.location?.value,
          status: ProcurementPurchaseOrderStatus.PartiallyReceived,
          items: [itemData],
        }
        const response = await receiveProcurementPurchaseOrderItem(order.id, data)
        queryClient.invalidateQueries({ queryKey: ['procurement-purchase-orders-receiving'] })
        setActiveOrder(response)
        onClose()
        toast.success('Item marked as partially received')
      } catch (error) {
        if (error instanceof AxiosError) {
          toast.error('Failed to mark as partially received')
        } else {
          toast.error(error.message)
        }
        return null
      }
    },
  })

  return (
    <>
      <Modal onClose={onClose}>
        <ModalContent>
          <ModalHeader>
            <BigText>Receive Item</BigText>
          </ModalHeader>
          <ModalBody>
            <Flex column gap='1rem'>
              <LocationSelect
                label='Location'
                placeholder='Select Location'
                value={values.location}
                onChange={(value) => setValues({ ...values, location: value })}
              />
              <Input
                label='Expiry Value'
                name='expiryValue'
                placeholder='30'
                type='number'
                value={values.expiryValue}
                onChange={(e) => setValues({ ...values, expiryValue: e.target.value })}
              />
              <Input
                label='Batch Code'
                name='batchCode'
                placeholder='C1443'
                value={values.batchCode}
                onChange={(e) => setValues({ ...values, batchCode: e.target.value })}
              />
              {item.procurementPurchaseRequestItem.inventoryItem.trackingMode !==
                InventoryTrackingMode.Serial && (
                <Input
                  label='Quantity'
                  name='quantity'
                  placeholder='100'
                  type='number'
                  value={values.quantity}
                  onChange={(e) => setValues({ ...values, quantity: e.target.value })}
                />
              )}

              <CalendarInput
                width='100%'
                placeholder='MFG Date'
                labelText='MFG Date'
                nomarg
                defaultValue={values.mfgDate}
                onChange={(date) => {
                  setValues({ ...values, mfgDate: date?.toISOString() })
                }}
              />
              {item.procurementPurchaseRequestItem.inventoryItem.trackingMode ===
                InventoryTrackingMode.Serial && (
                <>
                  <Input
                    label='Serial Number'
                    name='serialNumber'
                    placeholder='1234567890'
                    value={values.serialNumber}
                    onChange={(e) => setValues({ ...values, serialNumber: e.target.value })}
                  />
                  <CalendarInput
                    width='100%'
                    placeholder='Warranty Expiry'
                    labelText='Warranty Expiry'
                    nomarg
                    defaultValue={values.warrantyExpiry}
                    onChange={(date) => {
                      setValues({ ...values, warrantyExpiry: date?.toISOString() })
                    }}
                  />
                </>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Cancel</Button>
            <Button orange onClick={() => mutation.mutate()}>
              Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {mutation.isPending && <Spinner loading={mutation.isPending} />}
    </>
  )
}
